import {useOptimizedSchedulesStatsQuery} from '@domain/schedule'
import type {Env} from '@hconnect/common/hproduce'
import {getSupportedLanguages} from '@hconnect/common/utils'
import {useLoginContext, useRequiredUser} from '@hconnect/login'
import {CommonHeader, useThemeSettings} from '@hconnect/uikit/src/lib2'
import type {User, NavItem} from '@hconnect/uikit/src/lib2'
import {CommonHeaderGuidedTourProps} from '@hconnect/uikit/src/lib2/shell/header/CommonHeader'
import {getPlantName, useUpmPlantsContext, useUpmPlantContext} from '@hconnect/upm'
import {usePermissionsParsedQuery} from '@hproduce/permissions'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate, matchPath} from 'react-router'

import {subPageDynamicImportMap} from '../../dynamicImports'
import {mainRoutes, MainRoutes, getUrl, RouteName, resolvePageKey} from '../../routing'
import {useTrackPlannerEvent} from '../helpers/trackPlannerEvents'
import {usePageAvailability} from '../hooks/usePageAvailability'

type PlannerHeaderProps = {
  guidedTours?: CommonHeaderGuidedTourProps
}

export const PlannerHeader: React.FC<PlannerHeaderProps> = ({guidedTours}) => {
  const {t, i18n} = useTranslation()
  const {plantId: plantCode} = useUpmPlantContext()

  const upmPlants = useUpmPlantsContext()

  const trackPlannerEvent = useTrackPlannerEvent()

  const loggedUser = useRequiredUser()
  const {logout} = useLoginContext()
  const {data: permissions} = usePermissionsParsedQuery()

  const navigate = useNavigate()
  const location = useLocation()
  const pathName = location.pathname

  const pageAvailability = usePageAvailability(plantCode)
  const {data: optimizedSchedulesStats} = useOptimizedSchedulesStatsQuery({plantId: plantCode})

  const user: User = {
    email: loggedUser.eMail ?? undefined,
    name: loggedUser.name ?? undefined
  }

  const navItems: NavItem[] = useMemo(() => {
    if (!pageAvailability || !plantCode) return []

    return pageAvailability
      .filter((page) => page.hasPermission)
      .map((page) => ({
        label: t(`navItems.${page.routeName}`),
        dataTestId: `nav_item_${page.routeName}`,
        url: getUrl(page.path, {plantCode}),
        isDisabled: !page.isAvailable,
        showBadge:
          page.routeName === RouteName.OPTIMIZER &&
          optimizedSchedulesStats &&
          optimizedSchedulesStats.notAccepted > 0,
        id: `nav_item_${page.routeName}`
      }))
  }, [pageAvailability, plantCode, optimizedSchedulesStats, t])

  const handleNavigate = useCallback(
    (nextPage: string) => {
      const currentPageKey = resolvePageKey(pathName)
      const nextPageKey = resolvePageKey(nextPage)

      trackPlannerEvent({
        name: 'userMovesPage',
        fromPage: currentPageKey && t(`navItems.${currentPageKey}`, {lng: 'en-US'}),
        toPage: nextPageKey && t(`navItems.${nextPageKey}`, {lng: 'en-US'})
      })
      void navigate(nextPage)
    },
    [pathName, t, trackPlannerEvent, navigate]
  )

  const {themeMode} = useThemeSettings()

  if (!permissions || !plantCode) return null

  return (
    <CommonHeader
      appName="Planner"
      plant={{plantId: plantCode, plantName: getPlantName(upmPlants, plantCode)}}
      upmPlants={upmPlants}
      guidedTours={guidedTours}
      env={process.env.REACT_APP_STAGE as Env}
      permissions={permissions}
      navItems={navItems}
      navigate={handleNavigate}
      locationPath={pathName}
      supportedLanguages={getSupportedLanguages(i18n)}
      themeMode={themeMode}
      user={user}
      onLogout={() => void logout()}
      onNavItemHover={(url) => {
        const matchedRouteName = Object.entries(mainRoutes).find(([, path]) => {
          const match = matchPath(path, url)
          return Boolean(match)
        })?.[0] as keyof MainRoutes | undefined

        if (matchedRouteName) {
          void subPageDynamicImportMap[matchedRouteName]()
        }
      }}
    />
  )
}
